import React, { useEffect, useState, useMemo } from "react";
import { useForm, usePopover, useUserContext, useTable } from "hooks";
import { Page, PendingButton, FormGroup } from "components/common";
import { CardDeck, Table, Card, Button, UncontrolledTooltip, Input, Row, Form, Col, Alert, UncontrolledCollapse, UncontrolledButtonDropdown, DropdownMenu, DropdownItem, DropdownToggle } from "reactstrap";
import { Trans, useTranslation } from "react-i18next";
import { actions } from "react-redux-form";
import { formatCurrency, formatDate } from "services/util";
import { Link } from "react-router-dom";
import SummaryCard from "./SummaryCard";
import { NotificationManager } from "react-notifications";
import PayRunStatus from "./PayRunStatus";
import PaySlipDocument from "components/documents/PaySlip";
import PaySlipListDocument from "components/documents/PaySlipList"
import PaySlipDetail from "components/documents/PaySlipDetail";
import { withDocumentRenderer } from "components";
import { populateTemplate } from "services/templates";
import { reduce, get, concat, keys, uniq } from "lodash";
import BackButton from "components/refactoring/BackButton";
import { jsonToCsv } from "pages/Xero/Reports/ExportButton";
import { useConfirmModal, useMakePaymentModal } from "services";
import { HistoryAndNotes } from "components"

import { XeroClient } from "services";
import Select from "react-select";
import DatePicker from "react-datepicker"
import { languageNameMapping } from "constants/mappings";


const DownloadPayslip = withDocumentRenderer({
  footer: false,
  onDownload:
    ({ selectedOrganisation, client }) =>
      (getDocument) => {

        getDocument(
          {
            selectedOrganisation,
            client,
          },
          `pay_slip.pdf`
        );
      },
})(({ EmailDocument, DownloadDocument, DocumentRenderer, payslip }) => {
  return (
    <>
      <EmailDocument
        item={{
          Contact: {
            Name: payslip.employee__full_name,
            FirstName: payslip.employee__first_name,
            EmailAddress: payslip.employee__email_address,
          },
          Period: `${payslip.pay_run__start_date}-${payslip.pay_run__end_date}`,
          payslip,
        }}
        type="PAY_SLIP"
        className="mr-2 btn-icon" //email button
        compact={true}
      />
      <DownloadDocument className="mr-2 btn-icon" compact={true} />
      {/* //download button */}
      <DocumentRenderer component={PaySlipDocument} item={payslip} />
    </>
  );
});

const PrintAllPayslips = withDocumentRenderer({
  footer: false,
  onDownload:
    ({ selectedOrganisation, client, serial_number }) =>
      (getDocument) => {
        getDocument(
          {
            selectedOrganisation,
            client,
            serial_number,
          },
          `all_pay_slips_${serial_number}.pdf`
        );
      },
})(({ PrintDocument, DocumentRenderer, payslips }) => {
  const { t, i18n } = useTranslation();
  return (
    <td className="text-end">
      <PrintDocument className="mr-1" item={payslips} label={t("Print Payslips")} />
      <DocumentRenderer component={PaySlipListDocument} items={payslips} />
    </td>
  )
});

const DownloadPDF = withDocumentRenderer({
  footer: false,
  onDownload:
    ({ selectedOrganisation, client, serial_number }) =>
      (getDocument) => {
        getDocument(
          {
            selectedOrganisation,
            client,
            serial_number,
          },
          `all_pay_slips_${serial_number}.pdf`
        );
      },
})(({ DownloadDocument, DocumentRenderer, payslips }) => {
  const { t, i18n } = useTranslation();
  return (
    <td className="text-end">
      <DownloadDocument className="mr-1" compact={false} label={t("Download Payslips PDF")} />
      <DocumentRenderer component={PaySlipListDocument} items={payslips} />
    </td>
  )
});


const BulkEmailPayslips = withDocumentRenderer({ footer: false })(
  ({ DocumentRenderer, getDocument, payslips = [], selectedOrganisation, client, disabled, xeroClient, setSelected }) => {
    const { t } = useTranslation();
    const [pending, setPending] = useState(false);
    const { user } = useUserContext();

    const availableLangs = useMemo(() => {
      if (selectedOrganisation?.is_trial) return ["en"];
      const langs = selectedOrganisation?.subscribed_features?.hesaablanguage || [];
      return ["en", ...langs.filter((l) => l !== "en")];
    }, [selectedOrganisation]);

    const buildItem = (ps) => ({
      Contact: {
        Name: ps.employee__full_name,
        FirstName: ps.employee__first_name,
        EmailAddress: ps.employee__email_address,
      },
      Period: `${ps.pay_run__start_date}-${ps.pay_run__end_date}`,
      payslip: ps,
    });

    const fetchDefaultTemplate = async (lang) => {
      const templates = await xeroClient
        .get(`email_templates?language=${lang}&type=PAY_SLIP`)
        .get("data");
      return (
        templates.find(
          (i) => i.is_default && i.language === lang && i.type === "PAY_SLIP"
        ) || templates[0]
      );
    };

    const sendBulk = async (lang) => {
      if (!payslips.length) return;
      setPending(true);
      try {
        const defaultTemplate = await fetchDefaultTemplate(lang);
        if (!defaultTemplate) {
          NotificationManager.error(t("No email template found for PAY_SLIP"));
          return;
        }

        let sent = 0;
        let skipped = 0;
        const emailPromises = [];
        for (const ps of payslips) {
          const email = ps.employee__email_address;
          if (!email) {
            skipped += 1;
            continue;
          }

          const item = buildItem(ps);

          const subject = populateTemplate(defaultTemplate.subject, "PAY_SLIP", item);
          const content = populateTemplate(defaultTemplate.content, "PAY_SLIP", item);

          const filename = `pay_slip_${ps.employee__serial_number || ps.employee__full_name}.pdf`;

          const html = await getDocument(
            { selectedOrganisation, client, item: ps },
            null,
            lang
          );
          emailPromises.push(
            xeroClient.post("all/email_document", {
              email: [email],
              subject,
              content,
              template: html,
              filename,
              type: "PAY_SLIP",
              reply_to: selectedOrganisation?.email || user?.email || "",
            })
          );
        }
        const results = await Promise.all(
          emailPromises.map((p) => p.then(() => true).catch(() => false))
        );
        const fulfilledCount = results.filter(Boolean).length;
        sent += fulfilledCount;
        const failed = results.length - fulfilledCount;
        const summary = `${sent} ${t('sent')}, ${failed} ${t('failed')}, ${skipped} ${t('skipped')}`;
        if (sent) NotificationManager.success(`${sent} ${t('email(s) sent')}`);
        if (skipped) NotificationManager.warning(`${skipped} ${t('skipped (missing email)')}`);
        if (failed) NotificationManager.error(`${failed} ${t('failed to send')}`);
        if (!sent && !skipped && !failed) NotificationManager.info(t('No emails processed'));
        // Always show a compact summary
        NotificationManager.info(summary);
        setSelected({});
      } catch (e) {
        console.error(e);
        NotificationManager.error(t("Error sending payslips"));
      } finally {
        setPending(false);
      }
    };

    return (
      <td className="text-center mr-2">
        <UncontrolledButtonDropdown>
          <PendingButton
            pending={pending}
            component={DropdownToggle}
            caret
            color="primary"
            className="btn btn-sm mr-2"
            disabled={disabled || !payslips.length}
            translate={false}
          >
            <i className="fa fa-envelope mr-2" />
            <Trans>Email Payslips</Trans>
          </PendingButton>
          <DropdownMenu>
            {availableLangs?.map(lang => (
              languageNameMapping[lang] !== undefined && languageNameMapping[lang] !== "" && (
                <DropdownItem key={lang} onClick={() => sendBulk(lang)}>
                  <Trans>{languageNameMapping[lang]}</Trans>
                </DropdownItem>
              )
            ))}
          </DropdownMenu>
        </UncontrolledButtonDropdown>
        {/* Hidden renderer to generate per-payslip HTML */}
        <DocumentRenderer component={PaySlipDocument} />
      </td>
    );
  }
);


const DeleteCell = ({ onDelete: onDeleteProp, readOnly }) => {
  const [pending, setPending] = useState();
  const { setIsOpen, Popover, target } = usePopover();

  async function onDelete() {
    setPending(true);

    try {
      await onDeleteProp();
    } finally {
      setPending(false);
    }
  }



  return (
    <div className="">
      {!readOnly && (
        <>
          <PendingButton
            className="btn-icon btn mx-0"
            color="danger"
            size="sm"
            id={target}
            icon="fa fa-trash"
            onClick={() => setIsOpen(true)}
          />
          <Popover className="text-center">
            <div className="p-2">
              <Trans>
                Are you sure you want to remove this employee from this pay run?
              </Trans>
              <div className="d-flex justify-content-end mt-2">
                <PendingButton
                  onClick={onDelete}
                  pending={pending}
                  size="sm"
                  color="primary"
                >
                  Confirm
                </PendingButton>
              </div>
            </div>
          </Popover>
        </>
      )}
    </div>
  );
};

export const Edit = ({
  payroll,
  match,
  t,
  selectedOrganisation,
  history,
  client,
}) => {
  const url = "pay_runs";
  const { id } = match.params;
  const { loaded, load, setValue, value, dispatch } = useForm();
  const xero = XeroClient()

  const {
    TableContainer,
    data: payslips,
    PayrollTableDataFetch,
    TableSearch,
    ViewingRecords,
    Pagination,
    EditDeleteButtons,
    onDeleteItem,
    filters,
    refreshData,
  } = useTable({
    url: `${url}/${id}/pay_slips`,
    match,
    limit: 25,
  });

  const [pending, setPending] = useState();
  const [settings, setSettings] = useState();
  const [selected, setSelected] = useState({});
  const [selectedCount, setSelectedCount] = useState(0)
  const [allSelected, setAllSelected] = useState(false);
  const [earningTypes, setEarningTypes] = useState([]);
  const [filter, setFilter] = useState("")
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState('')
  const [paymentDate, setPaymentDate] = useState(null)
  const [reference, setReference] = useState(null)
  const [journals, setJournals] = useState(null)

  const [ setModalOpen, toggle, MainModal ] = useMakePaymentModal() 
  const [ SetApproveModal, ApproveToggle, ApproveModal, setApproveCallback ] = useConfirmModal()
  const [emptyFields, setEmptyFields] = useState(null)

  const [includeBills, setIncludeBills] = useState(true)


  const selectedIds = useMemo(() => {
    setSelectedCount(Object.entries(selected).filter(([key, value]) => value).length);

    return reduce(
      selected,
      (acc, i, key) => {
        if (i) {
          acc.push(parseInt(key, 10));
        }
        return acc;
      },
      []
    );
  }, [selected]);

  async function reload(initial=false) {
    const value = await load(payroll, url, id);

    const s = await payroll.get(`/settings/1/public`).get("data");
    const c_accounts = await xero.get("accounts", { params: { raw: `Status="ACTIVE" AND TYPE="BANK"` }, }).get("data").map((el) => ({
      label: `${el.Name}`,
      value: el.AccountID,
    }))
    if (value.status !== "Draft") {
      const _journals = await xero.get("manualjournals", { params: { raw: `Narration != null AND Narration.ToLower().Contains("(${value.id})")` }, }).get("data")
      setJournals(_journals)
    }
    setAccounts(c_accounts)
    setSettings(s);
    setSelectedAccount(s.credit_account)
    setValue(value);
    if (!initial)
      refreshData()
  }

  async function onDelete() {
    setPending(true);

    try {
      await payroll.delete(`pay_runs/${value.id}`);
      NotificationManager.success(t("Pay run successfully deleted"));
      history.replaceUp();
    } finally {
      setPending(false);
    }
  }

  async function EmployeesDelete(approved = false) { 
    if (approved) {
      NotificationManager.error(t("Cannot delete approved pay run"));
      return
    }

    setPending(true);
    try {
      const ids = Object.keys(selected).filter(id => selected[id]).join(',')
      await payroll.delete(`${url}/${id}/pay_slips/bulk_delete?ids=${ids}`);
      await reload();
      setSelected({})
      NotificationManager.success(t("Payslips deleted successfully"));
    } finally {
      setPending(false);
    }
  }

  async function onApprove(skip = false) {
    setPending(true);
    try {
      await payroll.post(`pay_runs/${value.id}/approve?skip=${skip}&bills=${includeBills}`);
      dispatch(actions.change("local.status", "Approved"));
      NotificationManager.success(t("Pay run successfully approved"));
      await reload()
    } catch (e) {
      if (e.response?.data?.code === 1) {
        setApproveCallback(() => () => { onApprove(skip = true) })
        setEmptyFields(e.response?.data?.message)
        ApproveToggle()
      } else {
        NotificationManager.error(`${t('Error while approving')} ${e.response?.data?.location} > ${e.response?.data?.message}`);
      }
    }
    finally {
      setPending(false);
    }
  }


  // const onMakePaymentToggle = () => {
  //   setCallback(() => () => { onMakePayment(selectedAccount, paymentDate, reference) })
  // }

  const preOnMakePayment = () => {
    // setCallback(() => () => { onMakePayment(selectedAccount, paymentDate, reference) })
    toggle()
  }

  async function onMakePayment(selectedAccount, paymentDate, reference) {
    setPending(true);
    try {
      const resp = await payroll
        .post(`pay_runs/${value.id}/make_payments`, {
          batch: !selectedIds.length,
          pay_slips: selectedIds,
          account: selectedAccount,
          payment_date: paymentDate && paymentDate.format("YYYY-MM-DD"),
          reference: reference,
        })
        .get("data");
      setValue(resp);
      NotificationManager.success(t("Payments successful"));
    } catch (ex) {
      const code = get(ex, "response.data.code");

      if (code === "EmployeeNotSynced") {
        NotificationManager.error(ex.response.data.detail);
      } else {
        throw ex;
      }
    } finally {
      setPending(false);
    }
  }

  useEffect(() => {
    reload(true);
  }, []);

  useEffect(() => {
    let earningTypes = [];
    payslips?.results?.map(
      (x) => (earningTypes = concat(earningTypes, keys(x.gross_breakdown)))
    );
    setEarningTypes(uniq(earningTypes));
  }, [payslips.results]);

  const onExport = () => {
    const paySlips = payslips?.results || [];
    const result = [[t("Payruns Report")]];

    const collectUniqueKeys = (type, keySelector) => {
      const set = new Set();
      paySlips.forEach(ps =>
        (ps.lines || []).forEach(line => {
          if (line.type === type) {
            const key = keySelector(line);
            if (key) set.add(key);
          }
        })
      );
      return Array.from(set);
    };

    const allowanceHeaders = collectUniqueKeys("ALLOWANCE", line => line.description || t("Allowance"));
    const deductionHeaders = collectUniqueKeys("DEDUCTION", line => line.subtype || line.description || t("Deduction"));

    const headers = [
      t("Serial Number"),
      t("Employee Serial Number"),
      t("Employee"),
      t("ID Number"),
      t("IBAN"),
      t("Salary"),
      t("Reimbursements"),
      t("Allowances"),
      ...allowanceHeaders,
      t("Deductions"),
      ...deductionHeaders,
      t("Tax"),
      t("Net"),
      t("Paid"),
    ];

    result.push([""]);
    result.push([`${t("Date")}: ${value.at_date}`]);
    result.push([""]);
    result.push(headers);

    const calculateTotals = (lines, type, keySelector) => {
      return (lines || []).reduce((acc, line) => {
        if (line.type === type) {
          const key = keySelector(line);
          acc[key] = (acc[key] || 0) + Number(line.amount || 0);
        }
        return acc;
      }, {});
    };

    for (const slip of paySlips) {
      const allowanceTotals = calculateTotals(slip.lines, "ALLOWANCE", line => line.description || t("Allowance"));
      const deductionTotals = calculateTotals(slip.lines, "DEDUCTION", line => line.subtype || line.description || t("Deduction"));

      result.push([
        value.serial_number,
        slip.employee__serial_number,
        slip.employee__full_name,
        String(slip.employee__id_number) || "",
        String(slip.employee__iban) || "",
        slip.gross_breakdown?.["Base salary"] || 0,
        slip.total_reimbursements,
        slip.total_allowances,
        ...allowanceHeaders.map(key => allowanceTotals[key] || 0),
        slip.total_deductions,
        ...deductionHeaders.map(key => deductionTotals[key] || 0),
        slip.total_tax,
        slip.net_pay,
        slip.total_paid,
      ]);
    }

    jsonToCsv(result, t("Payruns Report"));
  };


  if (!loaded) return null;

  const readOnly = value.status !== "Draft";
  const approved = value.status === "Approved";

  return (
    <>
      {/* END PRINTABLE */}
      <Page title={t("Pay Run Summary")} className="d-print-none break-after" style={{ breakAfter: "always" }}>
        <BackButton />
        <br /><br />
        <div style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: "1rem",
        }} >
          <p className="form-text text-muted"
            style={{ marginTop: "-1rem", fontSize: "85%" }} >
            {value.start_date} - {value.end_date}{" "}
            <PayRunStatus status={value.status} />
          </p>
          {
            payslips?.results &&
            <div className="acts d-flex">
              <DownloadPDF selectedOrganisation={selectedOrganisation} payslips={payslips?.results} client={client} serial_number={value.serial_number} />
              <td className="text-center">
                <PendingButton color="primary" onClick={onExport} className="btn btn-sm">
                  <i className="fas fa-file-excel mr-2"></i><Trans>Export CSV</Trans>
                </PendingButton>
              </td>
              <BulkEmailPayslips
                selectedOrganisation={selectedOrganisation}
                client={client}
                xeroClient={xero}
                payslips={(payslips?.results || []).filter(ps => selectedIds.length ? selectedIds.includes(ps.id) : false)}
                disabled={!selectedIds.length}
                setSelected={setSelected}
              />
              <PrintAllPayslips
                selectedOrganisation={selectedOrganisation}
                payslips={payslips?.results}
                client={client}
                serial_number={value.serial_number}
              />
            </div>
          }
        </div>

        <div className="border my-4 p-2 py-4">
          <CardDeck className="px-2">
            <SummaryCard title="Earnings" value={value.total_earnings} />
            <SummaryCard
              title="Reimbursements"
              value={value.total_reimbursements}
            />
            <SummaryCard title="Allowances" value={value.total_allowances} />
            <SummaryCard title="Deductions" value={value.total_deductions} />
            <SummaryCard title="Tax" value={value.total_tax} />
            <SummaryCard title="Net" value={value.total_pay} />
            <SummaryCard title="Paid" value={value.total_paid} />
          </CardDeck>

          <div className="search-container mt-5 mb-0">
            <Form className="row justify-content-start text-left">
              <Row className="row col mx-0 px-0">
                <Col className="col w-full">
                    <TableSearch label={t("Search by Name, Serial Number, Email...")} debounce={2000}/>
                </Col>
                <Col className="col-2">
                <div className="actions row">
                  {!approved && <div className="col d-flex justify-content-end">
                    <PendingButton onClick={() => EmployeesDelete(approved) } className="mr-0" color="danger" disabled={Object.keys(selected).length === 0 || approved}>
                      <Trans>Delete Payslips</Trans> {selectedCount > 0 ? `(${selectedCount})` : ''}
                    </PendingButton>
                  </div>}
                </div>
                </Col>
              </Row>
            </Form>
          </div>
          <TableContainer className="mt-4 inv-table v-middle text-center" responsive>
            <Table className="table-container table no-wrap v-middle text-center" >
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    onChange={(e) => {
                      const { checked } = e.target;

                      setSelected(
                        checked
                          ? payslips?.results.reduce((acc, i) => {
                            if (!i.paid) {
                              acc[i.id] = true;
                            }
                            return acc;
                          }, {})
                          : {}
                      );
                      setAllSelected(checked);
                    }}
                    checked={allSelected}
                  />
                </th>
                <th className="border-0">
                  <Trans>Employee</Trans> ({payslips?.results.filter((el) => el.employee__full_name.toLowerCase().includes(filter.toLowerCase())).length})
                </th>
                {earningTypes.map((x) => (
                  <th className="border-0" key={x}>
                    <Trans>{x}</Trans>
                  </th>
                ))}
                <th className="border-0">
                  <Trans>Reimbursements</Trans>
                </th>
                <th className="border-0">
                  <Trans>Allowances</Trans>
                </th>
                <th className="border-0">
                  <Trans>Deductions</Trans>
                </th>
                <th className="border-0">
                  <Trans>Tax</Trans>
                </th>
                <th className="border-0">
                  <Trans>Net</Trans>
                </th>
                <th className="border-0">
                  <Trans>Paid</Trans>
                </th>
                <th className="border-0">
                  <Trans>Remaining</Trans>
                </th>
                <th className="border-0">
                  <Trans>Actions</Trans>
                </th>
              </tr>
            </thead>
            <tbody>
              {payslips.results?.map((i) => (
                i.employee__full_name.toLowerCase().includes(filter.toLowerCase()) && (
                  <tr key={i.id}>
                    <td>
                      <input
                        type="checkbox"
                        onChange={(e) => {
                          const { checked } = e.target;

                          setSelected((v) => ({
                            ...v,
                            [i.id]: checked && !i.paid,
                          }));
                          setAllSelected(false);
                        }}
                        checked={selected[i.id] || false}
                      />
                    </td>
                    <td>
                      <Link to={`${match.url}/pay_slips/${i.id}`}>
                        {i.employee__full_name}
                      </Link>
                    </td>
                    {/* <td>{formatCurrency(i.total_earnings)}</td> */}
                    {earningTypes.map((x) => (
                      <td key={x}>{formatCurrency(i.gross_breakdown[x])}</td>
                    ))}
                    <td>{formatCurrency(i.total_reimbursements)}</td>
                    <td>{formatCurrency(i.total_allowances)}</td>
                    <td>{`(${formatCurrency(i.total_deductions)})`}</td>
                    <td>{formatCurrency(i.total_tax)}</td>
                    <td>{formatCurrency(i.net_pay)}</td>
                    <td>{formatCurrency(i.total_paid)}</td>
                    <td>{formatCurrency(i.net_pay - i.total_paid)}</td>
                    <td >
                      <div className="d-flex justify-content-center">
                        <Link id="UncontrolledTooltipExample"
                          className="d-print-none float-right btn-sm btn btn-primary mr-2 ml-0"
                          to={`${match.url}/pay_slips/${i.id}`}
                        >
                          <span className="fa fa-edit text-white" style={{ fontSize: '12px' }}></span>
                        </Link>
                        {
                          i.bill_id &&
                          <Link id="" className="float-right btn-sm btn btn-primary mr-2 ml-0" to={`/purchases/bills/view/${i.bill_id}`} >
                            <i className="fas fa-file-alt text-white" style={{ fontSize: '12px' }}></i>
                          </Link>
                        }
                        <UncontrolledTooltip placement="bottom" target="UncontrolledTooltipExample">
                          <Trans>Adjustment</Trans>
                        </UncontrolledTooltip>
                        <DownloadPayslip
                          selectedOrganisation={selectedOrganisation}
                          payslip={i}
                          client={client}
                        />
                        <DeleteCell
                          readOnly={readOnly}
                          onDelete={async () => {
                            await payroll.delete(`${url}/${id}/pay_slips/${i.id}`);
                            await reload();
                            NotificationManager.success(
                              t("Employee successfully removed")
                            );
                          }}
                        />

                      </div>
                    </td>
                  </tr>
                )
              ))}
            </tbody>
            </Table>
          </TableContainer>
          <PayrollTableDataFetch />
          {/* <PayrollTableDataFetch filters={filters} /> */}
          <div className="d-flex justify-content-between">
            <div className=""><ViewingRecords noCount /></div>
            <div className=""><Pagination /></div>
          </div>
        </div>
        <div className="payslips border mt-2">
          <button className="btn btn-link link text-success shadow-none py-3" id="payslips">
            <h6 className="mb-0"><i className="fas fa-angle-right mx-1"></i><Trans>View Payslips</Trans></h6>
          </button>
          <UncontrolledCollapse toggler="#payslips">
            {payslips?.results.map((i) => (
              i.employee__full_name.toLowerCase().includes(filter.toLowerCase()) && (
                <Card key={i.id} body className="mt-2">
                  <PaySlipDetail item={i} settings={settings} />
                </Card>
              )
            ))}
          </UncontrolledCollapse>
        </div>
        {journals && journals.length > 0 &&
          <div className="journals border mt-2">
            <button className="btn btn-link link text-success shadow-none py-3" id="journals">
              <h6 className="mb-0"><i className="fas fa-angle-right mx-1"></i><Trans>Related Manual Journals</Trans></h6>
            </button>
            <UncontrolledCollapse toggler="#journals">
              <Table size="sm" className="inv-table my-0">
                <thead>
                  <tr>
                    <th className="border-0"><Trans>Reference</Trans></th>
                    <th className="border-0"><Trans>Date</Trans></th>
                  </tr>
                </thead>
                <tbody>
                  {journals?.map((i, index) => (
                    <tr key={index}>
                      <td>
                        <Link to={`/accounts/manual_journals/all/${i.ManualJournalID}`}>
                          {i.Narration || "-"}
                        </Link>
                      </td>
                      {/* <td >
                      {formatDate(i.end_date)}
                  </td> */}
                      <td>{formatDate(i.Date)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </UncontrolledCollapse>
          </div>
        }
        {value.status === "Draft" &&
          <div className="my-3 mx-0">
            <input type="checkbox" checked={includeBills} onChange={() => setIncludeBills((old) => !old)} /><span className="ml-2" ><Trans>Include Bills</Trans></span>
          </div>
        }
        <div className="d-flex mt-2">
          {value.status === "Draft" && (
            <>
              <PendingButton
                pending={pending}
                onClick={onDelete}
                color="danger"
                className="ml-0"
              >
                Delete
              </PendingButton>
              <div className="mx-auto" />
              <PendingButton
                pending={pending}
                onClick={() => onApprove(false)}
                color="primary"
                className="mr-0"
              >
                Approve
              </PendingButton>
            </>
          )}
        </div>
        {value.status === "Approved" && (
          <div className="d-flex justify-content-end mt-2 mr-0">
            <PendingButton
              pending={pending}
              onClick={preOnMakePayment}
              color="primary"
              className="ml-auto mr-0"
            >
              {selectedIds.length
                ? "Make Individual Payments"
                : "Make Batch Payment"}
            </PendingButton>
          </div>
        )}

        <MainModal
          title={t('Payment Confirmation')}
          action={t("Confirm")}
          callback={() => onMakePayment(selectedAccount, paymentDate, reference)}
        >
          {selectedIds.length ?
            (<Alert color="warning">
              <Trans>You're paying the selected payslips</Trans>
            </Alert>)
            :
            (<Alert color="warning">
              <Trans>Are you sure you want to make a batch payment</Trans>
            </Alert>)
          }

          <Row>
            <Col className="col-12 my-2">
              <FormGroup
                label={t("Select a Bank Account")}
              >
                {accounts &&
                  <Select placeholder={`${t("Select a bank account")}`} options={accounts} value={selectedAccount || settings.credit_account} onChange={(e) => { setSelectedAccount(e?.value || "") }} Isclearable={false} />}
              </FormGroup>
            </Col>
          </Row>

          <Row>
            <Col className="col-12 my-2">
              <FormGroup
                label={t("Payment Date")}
              >
                <DatePicker
                  className="form-control"
                  selected={paymentDate}
                  onChange={(v) => { setPaymentDate(v) }}
                  dateFormat='YYYY-MM-DD'
                  placeholderText={t("Select Date")}
                />
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col className="col-12 my-2">
              <FormGroup
                label={t("Reference (Optional)")}
              >
                <Input
                  type="text"
                  name="reference"
                  value={reference || ''}
                  onChange={(e) => { setReference(e.target.value) }}
                  placeholder={t("Reference (Optional)")}
                />
              </FormGroup>
            </Col>
          </Row>
        </MainModal>

        <ApproveModal title={t('Empty Values Confirmation')} action={t("Confirm")}>
          <Alert color="warning">
            <Trans>The following employees have have empty values in their payslips, Do you want to skip the empty values?</Trans>
          </Alert>
          {emptyFields &&
            <div className="px-2">
              <p className="text-line">{emptyFields}</p>
            </div>
          }
        </ApproveModal>

        <hr />

        <HistoryAndNotes
          item={value}
          contentType={'payrun'}
          client={client}
          t={t}
        />

      </Page>
    </>
  );
};

export default Edit;
